// front/src/app/features/user/authSlice.js

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { showToast } from "../../../utils";
import { parseApiError } from "../../../utils/parseApiError";
import TokenService from "../../../api/TokenService";

import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

import { tt } from "../../../i18n/i18nBridge";

import {
  // Legacy
  login as legacyLogin,
  send_otp as legacySendOtp,
  verify_phone as legacyVerifyOtp,

  // New
  login_password,
  login_otp_send,
  login_otp_verify,
  signup,
  signup_otp_send,
  signup_otp_status,
  signup_otp_verify,

  // Forgot password
  forgot_password_send_otp,
  forgot_password_reset,

  // Email verify
  send_email_verification,
  verify_email,

  // Logout
  logout as logoutUser,
} from "../../../services/AuthServices/authServices";

const Alert = withReactContent(Swal);

export const initialState = {
  loading: false,
  error: null,
  errorKey: null,
  fieldErrors: null,
  is_email_code_send: false,
};

// -----------------------------
// helpers
// -----------------------------
const firstString = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v.trim() ? v.trim() : null;
  if (Array.isArray(v)) {
    for (const item of v) {
      const s = firstString(item);
      if (s) return s;
    }
    return null;
  }
  if (typeof v === "object") {
    for (const k of Object.keys(v)) {
      const s = firstString(v[k]);
      if (s) return s;
    }
    return null;
  }
  return null;
};

const normalizeFieldsObj = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  if (obj.detail && typeof obj.detail === "object") return obj.detail;
  return obj;
};

const pickFirstFieldError = (parsed) => {
  const fromParsed = normalizeFieldsObj(parsed?.fieldErrors);
  const fromExtra = normalizeFieldsObj(parsed?.extra?.fields);

  const obj = fromParsed || fromExtra || null;
  if (!obj) return null;

  const preferredKeys = [
    "phone_number",
    "username",
    "email",
    "accepted_terms",
    "accepted_privacy",
    "non_field_errors",
  ];

  for (const k of preferredKeys) {
    if (obj[k] != null) {
      const s = firstString(obj[k]);
      if (s) return s;
    }
  }

  const keys = Object.keys(obj);
  if (!keys.length) return null;
  return firstString(obj[keys[0]]);
};

const isUselessBackendMessage = (msg) => {
  const s = String(msg || "").trim().toLowerCase();
  return s === "validation error" || s === "validation_error";
};

const isAxiosGenericMessage = (msg) => {
  const s = String(msg || "").trim().toLowerCase();
  return (
    s.startsWith("request failed with status code") ||
    s === "network error" ||
    s === "timeout" ||
    s === "canceled" ||
    s === "cancelled"
  );
};

const isAxiosErrCode = (code) => {
  const s = String(code || "").trim();
  return s.startsWith("ERR_");
};

const throwHttpError = (status, data) => {
  const err = new Error(String(data?.message || "Request failed"));
  err.isAxiosError = true;
  err.response = { status, data };
  throw err;
};

const rejectApiError = (ex, fallbackMsg) => {
  // ✅ 1) DIRECT read from axios response (strongest & most reliable)
  const directData = ex?.response?.data;
  const directCode =
    (typeof directData?.code === "string" && directData.code.trim()) ||
    (typeof directData?.extra?.code === "string" && directData.extra.code.trim()) ||
    null;

  const directMessage =
    (typeof directData?.message === "string" && directData.message.trim()) ||
    (typeof directData?.detail === "string" && directData.detail.trim()) ||
    "";

  // ✅ 2) fallback to parser (for other shapes / wrappers)
  const parsed = parseApiError(ex) || {};

  let code =
    directCode ||
    (typeof parsed?.code === "string" && parsed.code.trim()) ||
    (typeof parsed?.extra?.code === "string" && parsed.extra.code.trim()) ||
    null;

  // ✅ never treat axios ERR_* as backend error codes
  if (code && isAxiosErrCode(code)) code = null;

  // prefer field message (best UX)
  const fieldMsg = pickFirstFieldError(parsed);

  // keep message only if meaningful + not axios generic
  const rawParsedMsg = isUselessBackendMessage(parsed?.message)
    ? ""
    : String(parsed?.message || "").trim();

  const parsedMsg = isAxiosGenericMessage(rawParsedMsg) ? "" : rawParsedMsg;

  const backendMsg = isUselessBackendMessage(directMessage)
    ? ""
    : (isAxiosGenericMessage(directMessage) ? "" : directMessage);

  const errorKey = code ? `api_errors.${code}` : null;

  const fallback = fallbackMsg || tt("common.request_failed", "Request failed.");

  // ✅ KEY FIX:
  // if we have a backend code and no real message, return empty message
  const message = fieldMsg || backendMsg || parsedMsg || (code ? "" : fallback);

  return {
    status: parsed?.status ?? ex?.response?.status ?? null,
    code,
    errorKey,
    message,
    fieldErrors: parsed.fieldErrors || parsed?.extra?.fields || null,
  };
};

const setTokens = (data) => {
  if (data?.access_token) TokenService.setAccessToken(data.access_token);
  if (data?.refresh_token) TokenService.setCustomField("refresh_token", data.refresh_token);
};

const clearTokens = () => {
  TokenService.removeAccessToken && TokenService.removeAccessToken();
  TokenService.removeUser && TokenService.removeUser();
  TokenService.removeCustomField("refresh_token");
  TokenService.removeCustomField("verify_token");
  TokenService.removeCustomField("email");
  TokenService.removeCustomField("token");
};

const digitsToAscii = (input) => {
  const s = String(input ?? "");
  const fa = "۰۱۲۳۴۵۶۷۸۹";
  const ar = "٠١٢٣٤٥٦٧٨٩";
  let out = "";
  for (const ch of s) {
    const faIdx = fa.indexOf(ch);
    if (faIdx >= 0) {
      out += String(faIdx);
      continue;
    }
    const arIdx = ar.indexOf(ch);
    if (arIdx >= 0) {
      out += String(arIdx);
      continue;
    }
    out += ch;
  }
  return out;
};

const normalizePhone = (raw) => {
  let s = digitsToAscii(raw).trim();
  s = s.replace(/[^\d+]/g, "");
  if (s.startsWith("00")) s = `+${s.slice(2)}`;
  if (s.includes("+") && !s.startsWith("+")) s = `+${s.replace(/\+/g, "")}`;

  const digitsOnly = s.replace(/[^\d]/g, "");

  if (digitsOnly.startsWith("989") && digitsOnly.length === 12) {
    return {
      isIran: true,
      phone989: digitsOnly,
      phoneE164: `+${digitsOnly}`,
      country_code: 98,
      national_number: `9${digitsOnly.slice(3)}`,
    };
  }

  if (s.startsWith("+98")) {
    const rest = s.slice(3).replace(/[^\d]/g, "");
    let nn = rest;
    if (nn.startsWith("0")) nn = nn.slice(1);
    if (nn.length === 10 && nn.startsWith("9")) {
      const phone989 = `989${nn.slice(1)}`;
      return {
        isIran: true,
        phone989,
        phoneE164: `+${phone989}`,
        country_code: 98,
        national_number: nn,
      };
    }
  }

  let d = digitsOnly;
  if (d.startsWith("98")) d = d.slice(2);
  if (d.startsWith("0")) d = d.slice(1);

  if (d.length === 10 && d.startsWith("9")) {
    const phone989 = `989${d.slice(1)}`;
    return {
      isIran: true,
      phone989,
      phoneE164: `+${phone989}`,
      country_code: 98,
      national_number: d,
    };
  }

  if (s.startsWith("+")) {
    const e164 = `+${s.slice(1).replace(/[^\d]/g, "")}`;
    return {
      isIran: false,
      phone989: null,
      phoneE164: e164,
      country_code: null,
      national_number: null,
    };
  }

  return {
    isIran: false,
    phone989: null,
    phoneE164: null,
    country_code: null,
    national_number: d || digitsOnly || null,
  };
};

const assertIranOnly = (n) => {
  if (n?.isIran) return;
  throw new Error(tt("auth.only_iran_supported", "Currently only Iranian mobile numbers are supported."));
};

const buildAuthPhonePayload = (rawPhone) => {
  const n = normalizePhone(rawPhone);
  assertIranOnly(n);

  const usernameLegacy = n.phone989;

  return {
    username: usernameLegacy,
    phone_number: usernameLegacy,
    phone_e164: n.phoneE164 || undefined,
    country_code: n.country_code ?? undefined,
    national_number: n.national_number ?? undefined,
    region: undefined,
    __normalized: n,
  };
};

const buildSignupPhonePayload = (rawPhone) => {
  const n = normalizePhone(rawPhone);

  // For non-Iran numbers we use digits-only username/phone identifier.
  const nonIranDigits = (n.phoneE164 || n.national_number || "").replace(/[^\d]/g, "");
  const username = n.isIran ? n.phone989 : nonIranDigits;

  if (!username) {
    throw new Error(tt("auth.phone_missing", "Phone number is missing."));
  }

  return {
    username,
    phone_number: username,
    phone_e164: n.phoneE164 || undefined,
    country_code: n.country_code ?? undefined,
    national_number: n.national_number ?? undefined,
    region: n.isIran ? undefined : "int",
    __normalized: n,
  };
};

const fd = (obj) => {
  const data = new FormData();
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) data.append(k, v);
  });
  return data;
};

// -----------------------------
// thunks
// -----------------------------
export const loginWithPassword = createAsyncThunk(
  "auth/loginWithPassword",
  async ({ phone_number, password, redirect = true, redirectTo = "/" }, { rejectWithValue }) => {
    try {
      const p = buildAuthPhonePayload(phone_number);

      const { status, data } = await login_password({
        phone_number: p.phone_number,
        password,
        phone_e164: p.phone_e164,
        country_code: p.country_code,
        national_number: p.national_number,
        region: p.region,
      });

      if (status < 200 || status >= 300) throwHttpError(status, data);

      TokenService.setUserName(p.username);
      setTokens(data);

      if (redirect) window.location.replace(redirectTo || "/");
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("auth.login_failed", "Login failed.")));
    }
  }
);

export const sendLoginOtp = createAsyncThunk(
  "auth/sendLoginOtp",
  async ({ phone_number }, { rejectWithValue }) => {
    try {
      const p = buildAuthPhonePayload(phone_number);

      const { status, data } = await login_otp_send(
        fd({
          username: p.username,
          phone_e164: p.phone_e164,
          country_code: p.country_code,
          national_number: p.national_number,
          region: p.region,
        })
      );

      if (status < 200 || status >= 300) throwHttpError(status, data);

      TokenService.setUserName(p.username);
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("auth.otp_request_failed", "OTP request failed.")));
    }
  }
);

export const verifyLoginOtp = createAsyncThunk(
  "auth/verifyLoginOtp",
  async ({ otp, redirect = true, redirectTo = "/" }, { rejectWithValue }) => {
    try {
      const phone = TokenService.getUserName();
      if (!phone) throw new Error(tt("auth.phone_missing", "Phone number is missing. Please request OTP again."));

      const { status, data } = await login_otp_verify(fd({ username: phone, otp }));
      if (status < 200 || status >= 300) throwHttpError(status, data);

      setTokens(data);
      if (redirect) window.location.replace(redirectTo || "/");
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("auth.otp_verify_failed", "OTP verification failed.")));
    }
  }
);

export const signupCreate = createAsyncThunk(
  "auth/signupCreate",
  async (
    { phone_number, password, email, accepted_terms, accepted_privacy, terms_version, privacy_version },
    { rejectWithValue }
  ) => {
    try {
      const p = buildSignupPhonePayload(phone_number);

      const payload = {
        phone_number: p.phone_number,
        password,
        phone_e164: p.phone_e164,
        country_code: p.country_code,
        national_number: p.national_number,
        region: p.region,
      };

      if (email) payload.email = email;
      if (accepted_terms !== undefined) payload.accepted_terms = !!accepted_terms;
      if (accepted_privacy !== undefined) payload.accepted_privacy = !!accepted_privacy;
      if (terms_version) payload.terms_version = terms_version;
      if (privacy_version) payload.privacy_version = privacy_version;

      const { status, data } = await signup(payload);

      if (status < 200 || status >= 300) throwHttpError(status, data);

      TokenService.setUserName(p.username);
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("auth.signup_failed", "Signup failed.")));
    }
  }
);

export const sendSignupOtp = createAsyncThunk(
  "auth/sendSignupOtp",
  async ({ delivery_channel } = {}, { rejectWithValue }) => {
    try {
      const phone = TokenService.getUserName();
      if (!phone) throw new Error(tt("auth.phone_missing", "Phone number is missing."));

      const payload = { username: phone };
      if (delivery_channel) payload.delivery_channel = delivery_channel;

      const { status, data } = await signup_otp_send(fd(payload));
      if (status < 200 || status >= 300) throwHttpError(status, data);
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("auth.otp_request_failed", "OTP request failed.")));
    }
  }
);

export const getSignupOtpStatus = createAsyncThunk(
  "auth/getSignupOtpStatus",
  async (_, { rejectWithValue }) => {
    try {
      const phone = TokenService.getUserName();
      if (!phone) throw new Error(tt("auth.phone_missing", "Phone number is missing."));

      const { status, data } = await signup_otp_status(fd({ username: phone }));
      if (status < 200 || status >= 300) throwHttpError(status, data);
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("common.request_failed", "Request failed.")));
    }
  }
);

export const verifySignupOtp = createAsyncThunk(
  "auth/verifySignupOtp",
  async ({ otp, redirect = true }, { rejectWithValue }) => {
    try {
      const phone = TokenService.getUserName();
      if (!phone) throw new Error(tt("auth.phone_missing", "Phone number is missing."));

      const { status, data } = await signup_otp_verify(fd({ username: phone, otp }));
      if (status < 200 || status >= 300) throwHttpError(status, data);

      setTokens(data);
      if (redirect) window.location.replace("/");
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("auth.otp_verify_failed", "OTP verification failed.")));
    }
  }
);

export const sendResetOtp = createAsyncThunk(
  "auth/sendResetOtp",
  async ({ phone_number }, { rejectWithValue }) => {
    try {
      const p = buildSignupPhonePayload(phone_number);

      const { status, data } = await forgot_password_send_otp(
        fd({
          phone_number: p.phone_number,
          phone_e164: p.phone_e164,
          country_code: p.country_code,
          national_number: p.national_number,
          region: p.region,
        })
      );

      if (status < 200 || status >= 300) throwHttpError(status, data);

      TokenService.setUserName(p.username);
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("auth.otp_request_failed", "OTP request failed.")));
    }
  }
);

export const resetPasswordWithToken = createAsyncThunk(
  "auth/resetPasswordWithToken",
  async ({ otp, new_password, new_password_confirm, redirect = true }, { rejectWithValue }) => {
    try {
      const phone = TokenService.getUserName();
      if (!phone) throw new Error(tt("auth.phone_missing", "Phone number is missing. Please request OTP again."));

      const payload = fd({ phone_number: phone, otp, new_password, new_password_confirm });

      const { status, data } = await forgot_password_reset(payload);
      if (status < 200 || status >= 300) throwHttpError(status, data);

      if (redirect) window.location.replace("/signin");
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("common.request_failed", "Request failed.")));
    }
  }
);

export const logout = createAsyncThunk("auth/logout", async (_, { rejectWithValue }) => {
  try {
    const isRtl =
      document?.documentElement?.getAttribute("dir") === "rtl" ||
      document?.body?.getAttribute("dir") === "rtl" ||
      document?.body?.classList?.contains("rtl");

    const copy = isRtl
      ? {
          title: tt("auth.logout_title_fa", "خروج از حساب"),
          text: tt("auth.logout_text_fa", "آیا مطمئن هستید که می‌خواهید خارج شوید؟"),
          confirm: tt("auth.logout_confirm_fa", "خروج"),
          cancel: tt("auth.logout_cancel_fa", "انصراف"),
          success: tt("auth.logout_success_fa", "با موفقیت خارج شدید."),
        }
      : {
          title: tt("auth.logout_title", "Sign out"),
          text: tt("auth.logout_text", "Are you sure you want to sign out?"),
          confirm: tt("auth.logout_confirm", "Sign out"),
          cancel: tt("auth.logout_cancel", "Cancel"),
          success: tt("auth.logout_success", "Signed out successfully."),
        };

    const { isConfirmed } = await Alert.fire({
      icon: "warning",
      title: copy.title,
      text: copy.text,
      showCancelButton: true,
      confirmButtonText: copy.confirm,
      cancelButtonText: copy.cancel,
      reverseButtons: isRtl,
      focusCancel: true,
      showCloseButton: true,
      allowOutsideClick: () => !Swal.isLoading(),
      allowEscapeKey: () => !Swal.isLoading(),
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        const refresh_token = TokenService.getCustomField("refresh_token");
        try {
          if (refresh_token) {
            const data = new FormData();
            data.append("refresh", refresh_token);
            await logoutUser(data);
          }
          return true;
        } catch (e) {
          return true;
        }
      },
      customClass: {
        popup: "swal-premium",
        title: "swal-premium__title",
        htmlContainer: "swal-premium__text",
        confirmButton: "swal-premium__confirm",
        cancelButton: "swal-premium__cancel",
      },
    });

    if (isConfirmed) {
      clearTokens();
      showToast("success", copy.success);
      window.location.replace("/signin");
    }

    return { success: isConfirmed === true };
  } catch (ex) {
    clearTokens();
    window.location.replace("/signin");
    return rejectWithValue(rejectApiError(ex, tt("auth.logout_failed", "Logout failed.")));
  }
});

export const sendEmailVerificationCode = createAsyncThunk(
  "auth/sendEmailVerificationCode",
  async (_, { rejectWithValue }) => {
    try {
      const { status, data } = await send_email_verification();
      if (status < 200 || status >= 300) throwHttpError(status, data);
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("common.request_failed", "Request failed.")));
    }
  }
);

export const emailVerifyCode = createAsyncThunk(
  "auth/emailVerifyCode",
  async ({ otp }, { rejectWithValue }) => {
    try {
      const { status, data } = await verify_email(fd({ otp }));
      if (status < 200 || status >= 300) throwHttpError(status, data);
      return data;
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("auth.verification_failed", "Verification failed.")));
    }
  }
);

// Legacy thunks unchanged...
export const sendOTPCode = createAsyncThunk(
  "auth/sendOTPCode",
  async ({ initialData, is_phone }, { rejectWithValue }) => {
    try {
      if (!is_phone) {
        const msg = tt(
          "auth.sms_only_disabled_email",
          "SMS-only login: email verification is disabled in this legacy flow."
        );
        showToast("error", msg);
        return rejectWithValue({ message: msg, fieldErrors: null, code: null, errorKey: null, status: null });
      }

      const { status, data } = await legacySendOtp(initialData);
      if (status >= 200 && status < 300) return data;

      throwHttpError(status, data);
    } catch (ex) {
      return rejectWithValue(rejectApiError(ex, tt("common.request_failed", "Request failed.")));
    }
  }
);

export const phoneNumberVerifyCode = createAsyncThunk(
  "auth/phoneNumberVerifyCode",
  async (payload, { rejectWithValue }) => {
    try {
      const code =
        payload instanceof FormData
          ? payload
          : payload?.code instanceof FormData
          ? payload.code
          : payload?.code;

      const redirect = payload instanceof FormData || payload?.redirect === undefined ? true : payload.redirect;

      const { status, data: verifyData } = await legacyVerifyOtp(code);
      if (status < 200 || status >= 300) throwHttpError(status, verifyData);

      const username = TokenService.getUserName();
      if (!username) throw new Error("Username is missing");

      const loginPayload = { token: verifyData?.token, username };
      const { status: loginStatus, data: loginData } = await legacyLogin(loginPayload);
      if (loginStatus < 200 || loginStatus >= 300) throwHttpError(loginStatus, loginData);

      setTokens(loginData);
      TokenService.removeCustomField("verify_token");

      if (redirect) window.location.replace("/");
      return { success: true, token: loginData?.access_token };
    } catch (error) {
      return rejectWithValue(rejectApiError(error, tt("auth.code_incorrect", "The entered code is incorrect.")));
    }
  }
);

export const clearAuthError = createAsyncThunk("auth/clearAuthError", async () => true);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const pending = (state) => {
      state.loading = true;
      state.error = null;
      state.errorKey = null;
      state.fieldErrors = null;
    };

    const rejected = (state, action) => {
      state.loading = false;

      if (action.payload && typeof action.payload === "object") {
        const payload = action.payload;

        const key =
          typeof payload.errorKey === "string" && payload.errorKey.trim()
            ? payload.errorKey.trim()
            : null;

        const rawMsg =
          typeof payload.message === "string" && payload.message.trim()
            ? payload.message.trim()
            : "";

        const safeMsg = isAxiosGenericMessage(rawMsg) ? "" : rawMsg;

        state.errorKey = key;
        state.error = safeMsg;
        state.fieldErrors = payload.fieldErrors || null;
        return;
      }

      state.errorKey = null;
      state.error = tt("common.request_failed", "Request failed.");
      state.fieldErrors = null;
    };

    const fulfilled = (state) => {
      state.loading = false;
      state.error = null;
      state.errorKey = null;
      state.fieldErrors = null;
    };

    builder
      .addCase(clearAuthError.fulfilled, (state) => {
        state.error = null;
        state.errorKey = null;
        state.fieldErrors = null;
      })
      .addCase(sendEmailVerificationCode.fulfilled, (state) => {
        state.is_email_code_send = true;
      })
      .addCase(sendEmailVerificationCode.rejected, (state) => {
        state.is_email_code_send = false;
      })

      .addCase(loginWithPassword.pending, pending)
      .addCase(loginWithPassword.fulfilled, fulfilled)
      .addCase(loginWithPassword.rejected, rejected)

      .addCase(sendLoginOtp.pending, pending)
      .addCase(sendLoginOtp.fulfilled, fulfilled)
      .addCase(sendLoginOtp.rejected, rejected)

      .addCase(verifyLoginOtp.pending, pending)
      .addCase(verifyLoginOtp.fulfilled, fulfilled)
      .addCase(verifyLoginOtp.rejected, rejected)

      .addCase(signupCreate.pending, pending)
      .addCase(signupCreate.fulfilled, fulfilled)
      .addCase(signupCreate.rejected, rejected)

      .addCase(sendSignupOtp.pending, pending)
      .addCase(sendSignupOtp.fulfilled, fulfilled)
      .addCase(sendSignupOtp.rejected, rejected)

      .addCase(verifySignupOtp.pending, pending)
      .addCase(verifySignupOtp.fulfilled, fulfilled)
      .addCase(verifySignupOtp.rejected, rejected)

      .addCase(sendResetOtp.pending, pending)
      .addCase(sendResetOtp.fulfilled, fulfilled)
      .addCase(sendResetOtp.rejected, rejected)

      .addCase(resetPasswordWithToken.pending, pending)
      .addCase(resetPasswordWithToken.fulfilled, fulfilled)
      .addCase(resetPasswordWithToken.rejected, rejected)

      .addCase(sendOTPCode.pending, pending)
      .addCase(sendOTPCode.fulfilled, fulfilled)
      .addCase(sendOTPCode.rejected, rejected)

      .addCase(phoneNumberVerifyCode.pending, pending)
      .addCase(phoneNumberVerifyCode.fulfilled, fulfilled)
      .addCase(phoneNumberVerifyCode.rejected, rejected);
  },
});

export default authSlice.reducer;
